import type { FC } from "react";
import React from "react";
import { Virtuoso } from "react-virtuoso";

import { EmptyView, ErrorView } from "@react/foundation/placeholder";
import { useInfiniteQuery } from "@react/foundation/query";

import { useQueries } from "../../../../shared/hooks/useQueries.ts";

import { GithubCommentCard } from "./GithubCommentCard.tsx";
import { GithubCommentCardSkeleton } from "./GithubCommentCardSkeleton.tsx";

interface GithubCommentListProps {
  readonly githubRepoId: number;
}

export const GithubCommentList: FC<GithubCommentListProps> = ({
  githubRepoId,
}) => {
  const { getMemoryGithubCommentsQueryOptions } = useQueries();
  const githubCommentsQueryOptions = getMemoryGithubCommentsQueryOptions({
    githubRepoId,
    order: "desc",
  });

  const comments = useInfiniteQuery(githubCommentsQueryOptions.infinite);

  if (comments.status === "pending") {
    return (
      <>
        <GithubCommentCardSkeleton className="mb-2" />
        <GithubCommentCardSkeleton className="mb-2" />
        <GithubCommentCardSkeleton className="mb-2" />
      </>
    );
  }

  if (comments.status === "error") {
    return <ErrorView description="Failed to load comments." />;
  }

  if (comments.items.length === 0) {
    return (
      <EmptyView
        description="Comments from this repository will appear here."
        title="No comments yet"
      />
    );
  }

  return (
    <Virtuoso
      computeItemKey={(_, comment) => comment.id}
      data={comments.items}
      endReached={comments.loadNextPage}
      itemContent={(_, comment) => (
        <GithubCommentCard className="mb-2" comment={comment} />
      )}
      useWindowScroll
    />
  );
};
